/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { Link } from 'react-router-dom';
import {
  SEO,
  ButtonLink,
  serviceIcons,
  ServiceKey,
  LightbulbIcon,
  ChevronRightIcon,
} from '../../components/common';
import { colors, borderRadius, shadows, typography, transitions } from '../../styles/theme';

export interface GuideStep {
  id: number;
  title: string;
  description: string;
  image: string;
}

// Rehberin vurgu renkleri: hero gradyanı, adım numaraları ve ipucu kutusu
export interface GuideAccent {
  from: string;
  to: string;
  soft: string;
  border: string;
  strong: string;
  text: string;
}

interface GuideCta {
  title: string;
  text: string;
  label: string;
  /** Harici bağlantı */
  href?: string;
  /** Site içi bağlantı */
  link?: string;
  tone: keyof typeof ctaTones;
}

interface GuideLayoutProps {
  service: ServiceKey;
  /** Hero'nun altına taşan tanıtım posteri (public/images/services) */
  poster?: { src: string; alt: string };
  seo: { title: string; description: string; keywords: string[] };
  breadcrumb: string;
  title: string;
  subtitle: string;
  accent: GuideAccent;
  steps: GuideStep[];
  tipsTitle: string;
  tips: string[];
  cta: GuideCta;
}

const guides: { key: ServiceKey; title: string; path: string; color: string; iconColor: string }[] = [
  { key: 'cekici', title: 'Çekici', path: '/nasil-kullanilir/cekici', color: colors.primary[100], iconColor: colors.primary[600] },
  { key: 'vinc', title: 'Vinç', path: '/nasil-kullanilir/vinc', color: colors.secondary[100], iconColor: colors.secondary[600] },
  { key: 'nakliye', title: 'Nakliye', path: '/nasil-kullanilir/nakliye', color: '#fef3c7', iconColor: '#d97706' },
  { key: 'yol-yardim', title: 'Yol Yardım', path: '/nasil-kullanilir/yol-yardim', color: '#dcfce7', iconColor: '#16a34a' },
  { key: 'servis', title: 'Servis', path: '/nasil-kullanilir/servis', color: colors.accent[100], iconColor: colors.accent[600] },
  { key: 'vip-tasima', title: 'VIP Taşıma', path: '/nasil-kullanilir/vip-tasima', color: colors.premium[100], iconColor: colors.premium[700] },
  { key: 'surucu', title: 'Sürücü', path: '/nasil-kullanilir/surucu', color: colors.secondary[100], iconColor: colors.secondary[700] },
];

const ctaTones = {
  primary: { from: colors.primary[500], to: colors.primary[700], button: colors.primary[700] },
  secondary: { from: colors.secondary[500], to: colors.secondary[700], button: colors.secondary[600] },
  accent: { from: colors.accent[500], to: colors.accent[700], button: colors.accent[700] },
  premium: { from: colors.premium[600], to: colors.neutral[900], button: colors.premium[700] },
};

const pageStyles = css`
  background: ${colors.background.secondary};
`;

const heroStyles = css`
  color: white;
  padding: 3rem 1rem;
  text-align: center;

  @media (min-width: 768px) {
    padding: 4rem 2rem;
  }
`;

const heroContentStyles = css`
  max-width: 800px;
  margin: 0 auto;

  .breadcrumb {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    margin-bottom: 1.5rem;
    font-size: ${typography.fontSize.sm};
    color: rgba(255, 255, 255, 0.85);

    a {
      color: white;
      text-decoration: none;

      &:hover {
        color: white;
        text-decoration: underline;
      }
    }

    svg {
      width: 14px;
      height: 14px;
      opacity: 0.7;
    }
  }

  .icon {
    width: 88px;
    height: 88px;
    margin: 0 auto 1.25rem;
    border-radius: ${borderRadius.full};
    background: rgba(255, 255, 255, 0.15);
    border: 1px solid rgba(255, 255, 255, 0.25);
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
      width: 48px;
      height: 48px;
    }
  }

  h1 {
    font-family: ${typography.fontFamily.heading};
    font-size: ${typography.fontSize['2xl']};
    font-weight: ${typography.fontWeight.extrabold};
    margin-bottom: 0.75rem;

    @media (min-width: 768px) {
      font-size: ${typography.fontSize['4xl']};
    }
  }

  p {
    font-size: ${typography.fontSize.base};
    color: rgba(255, 255, 255, 0.9);
    margin: 0;

    @media (min-width: 768px) {
      font-size: ${typography.fontSize.lg};
    }
  }
`;

const posterStyles = css`
  position: relative;
  z-index: 2;
  max-width: 360px;
  margin: -2.5rem auto 0;
  padding: 0 1rem;

  img {
    display: block;
    width: 100%;
    border-radius: ${borderRadius['2xl']};
    box-shadow: ${shadows['2xl']};
  }
`;

const sectionStyles = css`
  max-width: 1200px;
  margin: 0 auto;
  padding: 3rem 1rem;

  @media (min-width: 768px) {
    padding: 4rem 2rem;
  }
`;

const stepCardStyles = css`
  background: white;
  border-radius: ${borderRadius.xl};
  overflow: hidden;
  box-shadow: ${shadows.md};
  transition: all ${transitions.normal};
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${shadows.xl};
  }
`;

// Ekran görüntüleri farklı oranlarda; kırpmadan tamamı görünsün diye sabit oranlı
// alanın içine sığdırılır (iç kutu mutlak konumlu, böylece max-height çözümlenir)
const previewStyles = css`
  position: relative;
  aspect-ratio: 9 / 14;
  border-bottom: 1px solid ${colors.neutral[100]};

  .frame {
    position: absolute;
    inset: 0.75rem;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    border-radius: ${borderRadius.md};
    box-shadow: ${shadows.md};
  }
`;

const cardContentStyles = css`
  padding: 1rem;
  text-align: center;
  flex: 1;

  @media (min-width: 768px) {
    padding: 1.25rem 1rem;
  }

  .step-number {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    color: white;
    border-radius: ${borderRadius.full};
    font-weight: ${typography.fontWeight.bold};
    font-size: ${typography.fontSize.sm};
    margin-bottom: 0.5rem;
  }

  h3 {
    font-family: ${typography.fontFamily.heading};
    font-size: ${typography.fontSize.base};
    font-weight: ${typography.fontWeight.bold};
    color: ${colors.text.primary};
    margin-bottom: 0.4rem;
  }

  p {
    color: ${colors.text.secondary};
    font-size: ${typography.fontSize.sm};
    line-height: 1.5;
    margin: 0;
  }
`;

const tipBoxStyles = css`
  border: 1px solid;
  border-radius: ${borderRadius.xl};
  padding: 1.5rem;
  margin-top: 3rem;
  display: flex;
  align-items: flex-start;
  gap: 1rem;

  .tip-icon {
    width: 44px;
    height: 44px;
    flex-shrink: 0;
    border-radius: ${borderRadius.full};
    background: white;
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
      width: 22px;
      height: 22px;
    }
  }

  h2 {
    font-family: ${typography.fontFamily.heading};
    font-size: ${typography.fontSize.lg};
    font-weight: ${typography.fontWeight.bold};
    margin-bottom: 0.75rem;
  }

  ul {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding-left: 1.1rem;
  }

  li {
    list-style: disc;
    font-size: ${typography.fontSize.sm};
    line-height: 1.6;
  }
`;

const ctaSectionStyles = css`
  border-radius: ${borderRadius['2xl']};
  padding: 2.5rem 1.5rem;
  text-align: center;
  color: white;
  margin-top: 2rem;
  box-shadow: ${shadows.lg};

  @media (min-width: 768px) {
    padding: 3rem 2rem;
  }

  h2 {
    font-family: ${typography.fontFamily.heading};
    font-size: ${typography.fontSize.xl};
    font-weight: ${typography.fontWeight.bold};
    margin-bottom: 0.75rem;

    @media (min-width: 768px) {
      font-size: ${typography.fontSize['2xl']};
    }
  }

  p {
    color: rgba(255, 255, 255, 0.95);
    margin-bottom: 1.5rem;
  }
`;

const otherServicesStyles = css`
  margin-top: 3rem;
  padding-top: 3rem;
  border-top: 1px solid ${colors.neutral[200]};

  h2 {
    font-family: ${typography.fontFamily.heading};
    font-size: ${typography.fontSize.xl};
    font-weight: ${typography.fontWeight.bold};
    color: ${colors.text.primary};
    text-align: center;
    margin-bottom: 2rem;
  }
`;

const otherServicesGridStyles = css`
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(2, 1fr);

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const otherServiceCardStyles = css`
  background: white;
  border-radius: ${borderRadius.xl};
  padding: 1.5rem 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  text-align: center;
  text-decoration: none;
  box-shadow: ${shadows.md};
  border: 2px solid transparent;
  transition: all ${transitions.fast};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${shadows.lg};
    border-color: ${colors.primary[200]};
  }

  .icon {
    width: 56px;
    height: 56px;
    border-radius: ${borderRadius.full};
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
      width: 30px;
      height: 30px;
    }
  }

  .label {
    font-size: ${typography.fontSize.sm};
    font-weight: ${typography.fontWeight.semibold};
    color: ${colors.text.primary};
  }
`;

export default function GuideLayout({
  service,
  poster,
  seo,
  breadcrumb,
  title,
  subtitle,
  accent,
  steps,
  tipsTitle,
  tips,
  cta,
}: GuideLayoutProps) {
  const HeroIcon = serviceIcons[service];
  const ctaTone = ctaTones[cta.tone];
  const otherGuides = guides.filter((guide) => guide.key !== service);
  // 5 adımlı rehberde tek satır, 8 adımlıda 4'lü iki satır
  const desktopColumns = steps.length % 5 === 0 ? 5 : 4;
  const tabletColumns = desktopColumns === 5 ? 3 : 4;

  const stepsGridStyles = css`
    display: grid;
    gap: 1rem;
    grid-template-columns: repeat(2, 1fr);

    @media (min-width: 768px) {
      gap: 1.5rem;
      grid-template-columns: repeat(${tabletColumns}, 1fr);
    }

    @media (min-width: 1024px) {
      grid-template-columns: repeat(${desktopColumns}, 1fr);
    }
  `;

  return (
    <div css={pageStyles}>
      <SEO
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        url={`/nasil-kullanilir/${service}`}
      />

      <section
        css={[heroStyles, css`background: linear-gradient(135deg, ${accent.from} 0%, ${accent.to} 100%);`]}
      >
        <div css={heroContentStyles}>
          <nav className="breadcrumb" aria-label="Sayfa konumu">
            <Link to="/nasil-kullanilir">Nasıl Kullanılır?</Link>
            <ChevronRightIcon />
            <span>{breadcrumb}</span>
          </nav>
          <div className="icon">
            <HeroIcon />
          </div>
          <h1>{title}</h1>
          <p>{subtitle}</p>
        </div>
      </section>

      {poster && (
        <div css={posterStyles}>
          <img src={poster.src} alt={poster.alt} loading="lazy" />
        </div>
      )}

      <section css={sectionStyles}>
        <ol css={stepsGridStyles}>
          {steps.map((step) => (
            <li key={step.id} css={stepCardStyles}>
              <div css={previewStyles} style={{ background: accent.soft }}>
                <div className="frame">
                  <img src={step.image} alt={`${step.title} ekranı`} loading="lazy" />
                </div>
              </div>
              <div css={cardContentStyles}>
                <div className="step-number" style={{ background: accent.to }}>
                  {step.id}
                </div>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </div>
            </li>
          ))}
        </ol>

        <div
          css={tipBoxStyles}
          style={{ background: accent.soft, borderColor: accent.border, color: accent.text }}
        >
          <span className="tip-icon" style={{ color: accent.strong }}>
            <LightbulbIcon />
          </span>
          <div>
            <h2 style={{ color: accent.strong }}>{tipsTitle}</h2>
            <ul>
              {tips.map((tip) => (
                <li key={tip}>{tip}</li>
              ))}
            </ul>
          </div>
        </div>

        <div
          css={[ctaSectionStyles, css`background: linear-gradient(135deg, ${ctaTone.from} 0%, ${ctaTone.to} 100%);`]}
        >
          <h2>{cta.title}</h2>
          <p>{cta.text}</p>
          <ButtonLink
            to={cta.link}
            href={cta.href}
            variant="white"
            size="lg"
            css={css`color: ${ctaTone.button};`}
          >
            {cta.label}
          </ButtonLink>
        </div>

        <div css={otherServicesStyles}>
          <h2>{service === 'surucu' ? 'Müşteri Olarak Kullanım Rehberleri' : 'Diğer Rehberler'}</h2>
          <div css={otherServicesGridStyles}>
            {otherGuides.map((guide) => {
              const Icon = serviceIcons[guide.key];
              return (
                <Link key={guide.key} to={guide.path} css={otherServiceCardStyles}>
                  <span className="icon" style={{ background: guide.color, color: guide.iconColor }}>
                    <Icon />
                  </span>
                  <span className="label">{guide.title}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
